import getVWKey from "../helpers/getVWKey"
import Utils from "../utils";
import * as Events from "../utils/events";
import RenderManager from "../rendering/render-manager";
import AssetLibrary from "../assets/asset-library";
import Base2DElement from "./base-2d-element";

class MeanMedianModeInfo extends Base2DElement{
    constructor(options, parentLayer, uuid = THREE.Math.generateUUID()) {
        super(options, parentLayer, uuid);

        this._initInfoLabels();

        this._needsUpdate = {
            visible: true,
            parameter: true,
            values: true,
        };

        this._eventsUpdate = [
          { event: 'mmm_toggle_visibility', updateProperty: 'visible' },
          { event: 'mmm_toggle_info', updateProperty: 'visible' },
          { event: 'mmm_toggle_param', updateProperty: 'parameter' },
          { event: 'mmm_toggle_calc', updateProperty: 'values' },
          { event: 'mmm_change_values', updateProperty: 'values' },
        ];

        for (let eventInfo of this._eventsUpdate) {
            eventInfo.updateFunction = (mmm) => {
                if (mmm.uuid === this.mmm) {
                    this._needsUpdate[eventInfo.updateProperty] = true;
                }
            };

            Events.listen(eventInfo.event, eventInfo.updateFunction);
        }
    }

    _initValues(options) {
        this.mmm = options.mmm;
        this.type = "2DObject";
        this.type2d = "MMMInfo";
        this.index = options.index;
    }

    _createContainer() {
        const container = super._createContainer();

        container.classList.add("mean-median-mode-info");

        return container;
    }

    _getMMMData() {
        return Q3.data.assets.objects[this.mmm];
    }

    _getMMMObject() {
        return AssetLibrary.objects[this.mmm];
    }

    setMMM(mmm) {
        this.mmm = mmm;

        for (let property in this._needsUpdate) {
            this._needsUpdate[property] = true;
        }

        RenderManager.activeCamera.update = true;
    }

    redraw(width, height) {
        let update = this._needsUpdate;

        if (update.visible) {
            this._updateInfoLabelVisibility();
            update.visible = false;
        }

        if (update.parameter) {
            this._updateParamVisibility();
            update.parameter = false;
        }

        if (update.values) {
            this._updateInfoLabels();
            update.values = false;
        }
    }

    render(width, height) {
        if (!Q3.data.layers.layers2D[this.parentLayer.uuid] || this.disabled || Utils.queryParams.has("disable_2d")) return;

        if (!this.isMounted) {
            for (let property in this._needsUpdate) {
                this._needsUpdate[property] = true;
            }

            if (width > 0 && height > 0) {
                this.isMounted = true;
            }
        }

        this.redraw(width, height);
    }

    delete() {

        for (let eventInfo of this._eventsUpdate) {
            Events.listen(eventInfo.event, eventInfo.updateFunction);
        }

        this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 1);
        // this.parentLayer.domElement.removeChild(this.domElement);

        delete this.domElement;

        if (Q3.selected === this) {
            Q3.selected = null;
            Events.fire('selectchange');
        }

    }

    _initInfoLabels() {
        const table = document.createElement("table");
        this.domElement.appendChild(table);

        let params = {
            mean: { text: "Mean" },
            median: { text: "Median" },
            mode: { text: "Mode" },
        };

        for (let name in params) {
            let param = params[name];
            param.row = document.createElement("tr");
            param.nameCell = document.createElement("td");
            param.valueCell = document.createElement("td");
            param.valueResult = document.createElement("div");
            param.valueCalc = document.createElement("div");
            param.nameCell.classList.add("name");
            param.valueCell.classList.add("value");
            param.valueCalc.classList.add("calculation");
            param.row.appendChild(param.nameCell);
            param.row.appendChild(param.valueCell);
            param.valueCell.appendChild(param.valueResult);
            param.valueCell.appendChild(param.valueCalc);

            param.nameCell.innerText = param.text + ':';
        }

        table.appendChild(params.mean.row);
        table.appendChild(params.median.row);
        table.appendChild(params.mode.row);

        this._infoLabels = {
            container: this.domElement,
            table,
            params,
        };
    }

    _updateInfoLabels() {
        let mmm = this._getMMMObject();
        if (!mmm) return;

        let { mean, median, mode } = mmm.statValues;

        let removeTrailingZeros = (numStr) => {
            let index = numStr.length;
            while (numStr[index - 1] == '0') index--;
            if (numStr[index - 1] == '.') index--;
            return numStr.substring(0, index);
        };

        let meanStr = removeTrailingZeros((mean * 100).toFixed(2)) + 'cm';
        let medianStr = removeTrailingZeros((median * 100).toFixed(1)) + 'cm';
        let modeStr;
        if (mode.length != 0) {
            modeStr = mode.map(value => (value * 100).toFixed() + 'cm').join(', ');
        } else {
            modeStr = 'none';
        }

        let params = this._infoLabels.params;
        let values = { mean: meanStr, median: medianStr, mode: modeStr };

        for (let name in params) {
            let param = params[name];
            param.valueResult.innerText = values[name];
            param.valueCalc.classList.toggle("hidden", !mmm.showCalc);
        }

        if (mmm.showCalc) {
            let values = mmm.getValues();
            let valuesCm = values.map(x => (x * 100).toFixed());
            let meanCm = removeTrailingZeros((mean * 100).toFixed(2));
            params.mean.valueCalc.innerText = '(' + valuesCm.join(' + ') + ')/' + values.length + ' = ' + meanCm;

            let sortedValues = values.slice();
            sortedValues.sort((a, b) => a - b);

            {
                let sortedValuesCm = sortedValues.map(x => (x * 100).toFixed());

                let medianIndex = Math.floor(sortedValues.length / 2);
                if (sortedValues.length % 2 == 1) {
                    medianIndex = [medianIndex];
                } else {
                    medianIndex = [medianIndex - 1, medianIndex];
                }
                let medianValues = sortedValuesCm.slice();
                for (let index of medianIndex) {
                    medianValues[index] = '<span class="highlighted">' + medianValues[index] + '</span>';
                }
                let medianLines = [medianValues.join(', ')];
                if (medianIndex.length == 2) {
                    let val1 = sortedValues[medianIndex[0]], val2 = sortedValues[medianIndex[1]];
                    medianLines.push('(' + (val1 * 100).toFixed() + ' + ' + (val2 * 100).toFixed() + ')/2 = ' + removeTrailingZeros((median * 100).toFixed(1)));
                }
                params.median.valueCalc.innerHTML = medianLines.join('<br>');
            }

            {
                let modeValues = [];
                let index = 0;

                for (let [modeIndex, thisMode] of mode.entries()) {
                    while (index < sortedValues.length) {
                        let value = sortedValues[index];
                        let roundedValue = Math.round(value * 100) * 0.01;

                        if (roundedValue > thisMode) break;
                        index++;

                        let valStr = (value * 100).toFixed();
                        let valHtml;

                        if (roundedValue == thisMode) {
                            let hlClass = 'highlighted' + (modeIndex % 6 + 1);
                            valHtml = '<span class="' + hlClass + '">' + valStr + '</span>';
                        } else {
                            valHtml = valStr;
                        }
                        modeValues.push(valHtml);
                    }
                }

                while (index < sortedValues.length) {
                    let value = sortedValues[index++];
                    let roundedValue = Math.round(value * 100) * 0.01;
                    let valHtml = (value * 100).toFixed();
                    modeValues.push(valHtml);
                }

                params.mode.valueCalc.innerHTML = modeValues.join(', ');
            }
        }
    }

    _updateInfoLabelVisibility() {
        let mmm = this._getMMMObject();

        let showInfo = mmm && mmm.visible && mmm.showInfo;
        this._infoLabels.container.classList.toggle("hidden", !showInfo);
    }

    _updateParamVisibility() {
        let mmm = this._getMMMObject();
        if (!mmm) return;

        let params = this._infoLabels.params;
        for (let name in params) {
            let show = mmm.isParamShown(name);
            params[name].row.classList.toggle('hidden', !show);
        }
    }
}

export default MeanMedianModeInfo;
export { MeanMedianModeInfo };
